/**
 * Runtime environment detection shared by the frontend SPAs.
 *
 * Staging is the new-style `*.staging.artic.works` hosts; local is the dev
 * server on localhost / 127.0.0.1, or a `*.artic.localhost` host served by
 * `pnpm portless`; everything else — the bare `*.artic.works` production
 * domains — is production. Uses exact host / suffix matching (not a loose
 * substring) so look-alikes such as `not-staging.example` or
 * `localhost.example.com` are correctly treated as production.
 */
export type AppEnvironment = 'local' | 'staging' | 'production';

/**
 * Hostname suffix `pnpm portless` serves the local stack under, e.g.
 * `hub.artic.localhost`. In a linked git worktree portless prepends the branch
 * name, so the label order is `[<worktree>.]<service>.artic.localhost`.
 */
const PORTLESS_SUFFIX = '.artic.localhost';

export function getEnvironment(): AppEnvironment {
  if (typeof window === 'undefined') return 'production';
  const host = window.location.hostname;
  if (host === 'localhost' || host === '127.0.0.1') return 'local';
  if (host.endsWith(PORTLESS_SUFFIX)) return 'local';
  if (host === 'staging.artic.works' || host.endsWith('.staging.artic.works')) return 'staging';
  return 'production';
}

/**
 * A sibling service's base URL when this page is served through portless,
 * otherwise `fallback` unchanged.
 *
 * The service label is the one immediately before `artic.localhost`, and any
 * worktree prefix sits in front of it — so swapping that single label keeps us
 * inside the same worktree's set of services:
 *
 *   hub.artic.localhost         + 'api' -> https://api.artic.localhost
 *   eng-1.hub.artic.localhost   + 'api' -> https://eng-1.api.artic.localhost
 *   localhost:4000              + 'api' -> <fallback>
 *
 * Staying on the portless hostnames is not cosmetic: the auth cookies are
 * scoped to `artic.localhost`, so a call to `http://localhost:3000` from a
 * portless page would be a different host and carry no session.
 */
export function portlessServiceUrl(service: string, fallback: string): string {
  if (typeof window === 'undefined') return fallback;
  const { hostname, protocol, port } = window.location;
  if (!hostname.endsWith(PORTLESS_SUFFIX)) return fallback;

  const labels = hostname.slice(0, -PORTLESS_SUFFIX.length).split('.');
  labels[labels.length - 1] = service;
  // Port is normally empty (the proxy owns :443); it is set when portless fell
  // back to an unprivileged port such as :1355.
  return `${protocol}//${labels.join('.')}${PORTLESS_SUFFIX}${port ? `:${port}` : ''}`;
}

// ── Service URLs ─────────────────────────────────────────────────────────────

/** A first-party Artic service a frontend can link to or call. */
export type ArticService = 'api' | 'hub' | 'quality' | 'drawings' | 'dashboards' | 'realtime';

/**
 * THE single source of truth for where every service lives, per environment.
 * Add a service here and nowhere else — the duplicated per-app `getHubUrl()` /
 * `BACKEND_URLS` / `NEO_URLS` copies this replaced were the reason a portless
 * fix could land in one app's sidebar and silently miss another's.
 *
 * The hosted values are the Pulumi `domain` for each stack:
 *   packages/{artic-hub,quality,dashboards,drawing-control,nest-backend}/infrastructure
 *   infrastructure/stacks/realtime
 *
 * The `local` value is the fixed port `pnpm start` binds. Under `pnpm portless`
 * the local entry is replaced by the matching `*.artic.localhost` host, so the
 * key here is also the portless service label — keep the two in step.
 *
 * Origins only (no scheme juggling, no paths): `realtime` is stored as https and
 * scheme-swapped by `getRealtimeSocketUrl`, the one caller that needs ws(s).
 */
const SERVICE_ORIGINS: Record<ArticService, Record<AppEnvironment, string>> = {
  api: {
    local: 'http://localhost:3000',
    staging: 'https://api.staging.artic.works',
    production: 'https://api.artic.works',
  },
  hub: {
    local: 'http://localhost:4000',
    staging: 'https://hub.staging.artic.works',
    production: 'https://hub.artic.works',
  },
  quality: {
    local: 'http://localhost:5173',
    staging: 'https://quality.staging.artic.works',
    production: 'https://quality.artic.works',
  },
  drawings: {
    local: 'http://localhost:5174',
    staging: 'https://drawings.staging.artic.works',
    production: 'https://drawings.artic.works',
  },
  dashboards: {
    local: 'http://localhost:5175',
    staging: 'https://dashboards.staging.artic.works',
    production: 'https://dashboards.artic.works',
  },
  realtime: {
    local: 'http://localhost:4100',
    staging: 'https://realtime.staging.artic.works',
    production: 'https://realtime.artic.works',
  },
};

/**
 * Base origin of a service for the environment this page is running in — the
 * primitive every named getter below is built from.
 *
 * Locally this is the fixed `pnpm start` port, or the sibling
 * `*.artic.localhost` host when served through `pnpm portless` (worktree prefix
 * preserved). Callers should prefer the named getters; reach for this directly
 * only when the service is chosen at runtime.
 */
export function serviceUrl(service: ArticService): string {
  const env = getEnvironment();
  const origin = SERVICE_ORIGINS[service][env];
  return env === 'local' ? portlessServiceUrl(service, origin) : origin;
}

/** The nest-backend API. */
export const getApiUrl = (): string => serviceUrl('api');

/** The hub (artic-hub) — the legacy SPA that owns sign-in. */
export const getHubUrl = (): string => serviceUrl('hub');

/** The Quality SPA, a.k.a. Neo (`packages/quality`). */
export const getQualityUrl = (): string => serviceUrl('quality');

/** The Drawing Control SPA. */
export const getDrawingsUrl = (): string => serviceUrl('drawings');

/** The Dashboards SPA. */
export const getDashboardsUrl = (): string => serviceUrl('dashboards');

/**
 * The Phoenix realtime WebSocket endpoint, including its `/socket` path.
 * Same origin as `serviceUrl('realtime')` with the scheme swapped, so a plain
 * HTTP proxy (`portless --no-tls`) correctly yields `ws://` rather than `wss://`.
 */
export const getRealtimeSocketUrl = (): string =>
  `${serviceUrl('realtime').replace(/^http/, 'ws')}/socket`;

/**
 * Storybook. Deliberately NOT in `SERVICE_ORIGINS`: it has its own dev server
 * (and portless subdomain) locally, is served from a path on Neo's build on
 * staging, and is never published in production.
 */
export function getStorybookUrl(): string | undefined {
  const env = getEnvironment();
  if (env === 'production') return undefined;
  // Derived, not restated: the staging bundle is a path on Neo's own origin, so
  // it must follow `SERVICE_ORIGINS.quality` rather than pin the host twice.
  if (env === 'staging') return `${SERVICE_ORIGINS.quality.staging}/storybook/`;
  return portlessServiceUrl('storybook', 'http://localhost:6006');
}
